import { useEffect, useState } from "react"
import { useNavigate, useParams } from "react-router-dom"
import { useForm, Controller } from "react-hook-form"
import { zodResolver } from "@hookform/resolvers/zod"
import { z } from "zod"
import { AlertCircle, ChevronLeft, Loader2 } from "lucide-react"

import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import { Textarea } from "@/components/ui/textarea"
import { Switch } from "@/components/ui/switch"
import { CreatorPicker } from "@/components/CreatorPicker"
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select"
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card"
import { CategoryMultiSelect } from "@/components/CategoryMultiSelect"
import {
  CoverImageDrop,
  type CoverImageResult,
} from "@/components/CoverImageDrop"
import { useCategories } from "@/lib/queries/categories"
import {
  useCreateAlbum,
  useGetAlbum,
  useUpdateAlbum,
} from "@/lib/queries/albums"

// ───────────────────────────────────────────────────────────────────────
// PAGE: Album form (combined New + Edit)
// LEGACY: apps/admin/src/pages/AddAlbum.jsx
// API:    POST /admin/album_get    { id }                    (when editing)
//         POST /admin/album_create { ...form }              (when creating)
//         POST /admin/album_update { id, ...form }          (when editing)
//
// ─── Field parity (legacy ↔ v2) ──────────────────────────────────────
// Form fields → POST body:
//   title                   → title
//   description             → description
//   author                  → author
//   album_type              → album_type   (1=Free, 2=Paid; sent as number)
//   category_ids[]          → category_ids (array of category ObjectIds)
//   category_id             → category_id  (sent as the FIRST selected
//                              category_id, mirroring legacy fallback for
//                              backward compat with single-category data)
//   cover image upload      → S3_cover_image (raw s3response object)
//                            + cover_image_name (file_name)
//                            (NOTE: capital 'S' in S3_cover_image — preserved)
//   is_carousel             → is_carousel
//   is_notification         → is_notification (HIDDEN on edit; matches legacy)
//   notification_message    → notification_message (only when is_notification)
//   order                   → order         (number)
//   carousel_order          → carousel_order (number)
//
// Validation parity:
//   - Cover image is required ONLY when creating (legacy: !S3_cover && !params.id)
//   - On edit, the existing cover_image is shown as the initial preview;
//     submitting without uploading a new file omits S3_cover_image so the
//     backend keeps the existing one. (Legacy behavior matches.)
//
// On success: navigate("/albums") (legacy navigated to "/album").
// ───────────────────────────────────────────────────────────────────────

const schema = z
  .object({
    title: z.string().optional(),
    description: z.string().optional(),
    author: z.string().optional(),
    album_type: z.coerce.number().int().min(1).max(2),
    category_ids: z.array(z.string()),
    is_carousel: z.boolean(),
    is_notification: z.boolean(),
    notification_message: z.string().optional(),
    order: z.coerce.number().int().min(0),
    carousel_order: z.coerce.number().int().min(0),
    // V-Q5: optional metadata surfaced on ContentItem.metadata.
    producer: z.string().max(120).optional().default(""),
    speaker: z.string().max(120).optional().default(""),
    curator: z.string().max(120).optional().default(""),
    publicly_available: z.boolean().default(false),
    // V-Q10: admin soft-hide toggle. Same semantic as category — hides
    // from every user-facing surface (Home/Discover/coach feed/single-
    // item) while staying visible to admin. Reversible from the same
    // form.
    is_hidden: z.boolean().default(false),
  })

type FormValues = z.infer<typeof schema>

const DEFAULT_VALUES: FormValues = {
  title: "",
  description: "",
  author: "",
  album_type: 1,
  category_ids: [],
  is_carousel: true,
  // V-Q6: first-publish defaults to "notify followers." Admins can still
  // opt out via the toggle on CREATE. The toggle is hidden on EDIT and
  // `is_notification` is stripped from the EDIT payload below, so an
  // already-published album can't accidentally re-notify its followers.
  is_notification: true,
  notification_message: "",
  order: 0,
  carousel_order: 0,
  producer: "",
  speaker: "",
  curator: "",
  publicly_available: false,
  is_hidden: false,
}

export default function AlbumForm() {
  const { id } = useParams<{ id: string }>()
  const isEdit = !!id
  const navigate = useNavigate()

  const categoriesQ = useCategories()
  const albumQ = useGetAlbum(id)
  const createMut = useCreateAlbum()
  const updateMut = useUpdateAlbum()
  const submittingMut = isEdit ? updateMut : createMut

  const [coverResult, setCoverResult] = useState<CoverImageResult | null>(null)
  const [isUploading, setIsUploading] = useState(false)
  const [submitError, setSubmitError] = useState<string | null>(null)

  const {
    control,
    register,
    handleSubmit,
    watch,
    reset,
    formState: { errors },
  } = useForm<FormValues>({
    resolver: zodResolver(schema),
    defaultValues: DEFAULT_VALUES,
  })

  const isNotification = watch("is_notification")

  // Hydrate when editing.
  useEffect(() => {
    if (!isEdit || !albumQ.data) return
    const a = albumQ.data
    const idsFromArray = Array.isArray(a.category_ids)
      ? (a.category_ids.filter(
          (x): x is string => typeof x === "string"
        ) as string[])
      : []
    // Fallback: if category_ids is empty but category_id is set, seed it.
    let categoryIdsInit = idsFromArray
    if (categoryIdsInit.length === 0) {
      const single =
        typeof a.category_id === "string"
          ? a.category_id
          : a.category_id && typeof a.category_id === "object"
            ? a.category_id._id
            : undefined
      if (single) categoryIdsInit = [single]
    }

    reset({
      title: a.title ?? "",
      description: a.description ?? "",
      author: a.author ?? "",
      album_type:
        typeof a.album_type === "number"
          ? a.album_type
          : Number(a.album_type) === 2
            ? 2
            : 1,
      category_ids: categoryIdsInit,
      is_carousel: !!a.is_carousel,
      // is_notification + notification_message are hidden on edit, but we
      // initialize them to the existing values anyway (legacy hides the UI
      // but doesn't reset the stored values).
      is_notification: !!a.is_notification,
      notification_message: a.notification_message ?? "",
      order: typeof a.order === "number" ? a.order : 0,
      carousel_order:
        typeof a.carousel_order === "number" ? a.carousel_order : 0,
      producer: a.producer ?? "",
      speaker: a.speaker ?? "",
      curator: a.curator ?? "",
      publicly_available: !!a.publicly_available,
      is_hidden: !!a.is_hidden,
    })
  }, [isEdit, albumQ.data, reset])

  const existingCoverPreview = isEdit ? albumQ.data?.cover_image : undefined

  const onSubmit = handleSubmit(async (values) => {
    setSubmitError(null)

    // Cover required only on CREATE (legacy validation).
    if (!isEdit && !coverResult) {
      setSubmitError("Please upload a cover image.")
      return
    }

    const firstCategoryId = values.category_ids[0]

    // Base payload always sent.
    const payload: Parameters<typeof createMut.mutateAsync>[0] = {
      ...(isEdit && id ? { id } : {}),
      // Mirror legacy: send category_id as the first selected ID for
      // back-compat with old single-category records.
      category_id: firstCategoryId,
      category_ids: values.category_ids,
      title: values.title ?? "",
      description: values.description ?? "",
      author: values.author ?? "",
      album_type: values.album_type,
      is_carousel: values.is_carousel,
      // V-Q6: notification fields are CREATE-only. Stripping them on EDIT
      // means an admin republishing/editing an existing album cannot
      // re-page followers — the cron's "first notify wins" idempotency
      // (is_notified: true) backs this up server-side, but we strip
      // client-side too so the intent is explicit.
      ...(isEdit
        ? {}
        : {
            is_notification: values.is_notification,
            notification_message: values.is_notification
              ? values.notification_message ?? ""
              : "",
          }),
      order: values.order,
      carousel_order: values.carousel_order,
      producer: values.producer ?? "",
      speaker: values.speaker ?? "",
      curator: values.curator ?? "",
      publicly_available: values.publicly_available,
      is_hidden: values.is_hidden,
    }

    // Only include cover fields when the user picked a NEW file. Legacy
    // sends them as undefined when not picked; we omit them so the backend
    // preserves the existing cover.
    if (coverResult?.s3response) {
      payload.S3_cover_image = coverResult.s3response
      payload.cover_image_name = coverResult.file_name
    }

    try {
      const res = await submittingMut.mutateAsync(payload)
      if (!res?.success) {
        setSubmitError(res?.message || `Failed to ${isEdit ? "update" : "create"} album.`)
        return
      }
      navigate("/albums")
    } catch (err) {
      setSubmitError(
        err instanceof Error
          ? err.message
          : `Failed to ${isEdit ? "update" : "create"} album.`
      )
    }
  })

  const isLoadingPrefill = isEdit && albumQ.isLoading

  return (
    <div className="mx-auto max-w-3xl space-y-4">
      <div className="flex items-center gap-2">
        <Button asChild variant="ghost" size="sm">
          <a href="/albums">
            <ChevronLeft className="mr-1 h-4 w-4" /> Back to albums
          </a>
        </Button>
      </div>

      <Card>
        <CardHeader>
          <CardTitle>{isEdit ? "Edit album" : "Create album"}</CardTitle>
          <CardDescription>
            {isEdit
              ? albumQ.data?.title || "Loading…"
              : "Cover image is required."}
          </CardDescription>
        </CardHeader>
        <CardContent>
          {isEdit && albumQ.isError && (
            <div
              role="alert"
              className="mb-4 flex items-start gap-2 rounded-md border border-destructive/30 bg-destructive/5 p-3 text-sm text-destructive"
            >
              <AlertCircle className="mt-0.5 h-4 w-4 shrink-0" />
              <span>Failed to load this album. Try refreshing.</span>
            </div>
          )}

          {isLoadingPrefill ? (
            <div className="grid h-32 place-items-center text-muted-foreground">
              <Loader2 className="h-5 w-5 animate-spin" />
            </div>
          ) : (
            <form className="space-y-6" onSubmit={onSubmit} noValidate>
              {/* Categories */}
              <div className="space-y-2">
                <Label>Categories</Label>
                <Controller
                  control={control}
                  name="category_ids"
                  render={({ field }) => (
                    <CategoryMultiSelect
                      options={categoriesQ.data ?? []}
                      value={field.value}
                      onChange={field.onChange}
                      loading={categoriesQ.isLoading}
                      disabled={submittingMut.isPending}
                    />
                  )}
                />
              </div>

              {/* Title */}
              <div className="space-y-2">
                <Label htmlFor="title">Title</Label>
                <Input id="title" {...register("title")} />
              </div>

              {/* Description */}
              <div className="space-y-2">
                <Label htmlFor="description">Description</Label>
                <Textarea id="description" rows={4} {...register("description")} />
              </div>

              {/* Author — picker against the Creators directory */}
              <div className="space-y-2">
                <Label htmlFor="author">Author</Label>
                <Controller
                  control={control}
                  name="author"
                  render={({ field }) => (
                    <CreatorPicker
                      id="author"
                      value={field.value || ""}
                      onChange={field.onChange}
                      placeholder="Pick or create the author"
                    />
                  )}
                />
              </div>

              {/* Album type — Free/Paid */}
              <div className="space-y-2">
                <Label htmlFor="album_type">Album type</Label>
                <Controller
                  control={control}
                  name="album_type"
                  render={({ field }) => (
                    <Select
                      value={String(field.value)}
                      onValueChange={(v) => field.onChange(Number(v))}
                    >
                      <SelectTrigger id="album_type" className="w-48">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="1">Free</SelectItem>
                        <SelectItem value="2">Paid</SelectItem>
                      </SelectContent>
                    </Select>
                  )}
                />
              </div>

              {/* Cover image */}
              <div className="space-y-2">
                <Label>
                  Cover image{!isEdit && <span className="text-destructive"> *</span>}
                </Label>
                <CoverImageDrop
                  initialPreview={existingCoverPreview}
                  result={coverResult}
                  onResult={setCoverResult}
                  onUploadingChange={setIsUploading}
                  disabled={submittingMut.isPending}
                />
              </div>

              {/* Carousel toggle */}
              <div className="flex items-center justify-between gap-4 rounded-md border p-4">
                <div>
                  <Label htmlFor="is_carousel">Show in carousel</Label>
                  <p className="text-xs text-muted-foreground">
                    Featured albums appear in the home carousel.
                  </p>
                </div>
                <Controller
                  control={control}
                  name="is_carousel"
                  render={({ field }) => (
                    <Switch
                      id="is_carousel"
                      checked={field.value}
                      onCheckedChange={field.onChange}
                    />
                  )}
                />
              </div>

              {/* V-Q10: soft-hide toggle. Hides the album from every
                  user-facing surface (Home / Discover / coach feed /
                  single-item) while keeping it in the admin list.
                  Reversible. */}
              <div className="flex items-center justify-between gap-4 rounded-md border p-4">
                <div>
                  <Label htmlFor="is_hidden">Hide from users</Label>
                  <p className="text-xs text-muted-foreground">
                    Keeps this album out of the app while
                    work-in-progress. Toggle off to publish back.
                  </p>
                </div>
                <Controller
                  control={control}
                  name="is_hidden"
                  render={({ field }) => (
                    <Switch
                      id="is_hidden"
                      checked={!!field.value}
                      onCheckedChange={field.onChange}
                    />
                  )}
                />
              </div>

              {/* V-Q5: Metadata block. Surfaces on ContentItem.metadata
                  via the chapter info sheet on mobile. Admin leaves any
                  blank to omit from the response. */}
              <div className="space-y-3 rounded-md border p-4">
                <div>
                  <Label className="text-base">Metadata</Label>
                  <p className="text-xs text-muted-foreground">
                    Optional. Shown on the mobile info sheet — leave a
                    field blank to hide it.
                  </p>
                </div>
                <div className="grid gap-3 sm:grid-cols-3">
                  <div className="space-y-2">
                    <Label htmlFor="producer">Producer</Label>
                    <Input id="producer" placeholder="Area52" {...register("producer")} />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="speaker">Speaker</Label>
                    <Input id="speaker" placeholder="Bob Lazar" {...register("speaker")} />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="curator">Curator</Label>
                    <Input id="curator" placeholder="GoodTalk team" {...register("curator")} />
                  </div>
                </div>
                <div className="flex items-center justify-between gap-4">
                  <div>
                    <Label htmlFor="publicly_available">Publicly available</Label>
                    <p className="text-xs text-muted-foreground">
                      Show a "publicly available" indicator on the info
                      sheet (e.g. for content also free to listen
                      elsewhere).
                    </p>
                  </div>
                  <Controller
                    control={control}
                    name="publicly_available"
                    render={({ field }) => (
                      <Switch
                        id="publicly_available"
                        checked={!!field.value}
                        onCheckedChange={field.onChange}
                      />
                    )}
                  />
                </div>
              </div>

              {/* Notification toggle (CREATE only — matches legacy) */}
              {!isEdit && (
                <div className="space-y-3 rounded-md border p-4">
                  <div className="flex items-center justify-between gap-4">
                    <div>
                      <Label htmlFor="is_notification">Send notification</Label>
                      <p className="text-xs text-muted-foreground">
                        Push to subscribed users when this album goes live.
                      </p>
                    </div>
                    <Controller
                      control={control}
                      name="is_notification"
                      render={({ field }) => (
                        <Switch
                          id="is_notification"
                          checked={field.value}
                          onCheckedChange={field.onChange}
                        />
                      )}
                    />
                  </div>
                  {isNotification && (
                    <div className="space-y-2">
                      <Label htmlFor="notification_message">
                        Notification message
                      </Label>
                      <Textarea
                        id="notification_message"
                        rows={3}
                        {...register("notification_message")}
                      />
                    </div>
                  )}
                </div>
              )}

              {/* Order + Carousel Order */}
              <div className="grid gap-4 sm:grid-cols-2">
                <div className="space-y-2">
                  <Label htmlFor="order">Order</Label>
                  <Input
                    id="order"
                    type="number"
                    min={0}
                    {...register("order")}
                  />
                  {errors.order && (
                    <p className="text-xs text-destructive">{errors.order.message}</p>
                  )}
                </div>
                <div className="space-y-2">
                  <Label htmlFor="carousel_order">Carousel order</Label>
                  <Input
                    id="carousel_order"
                    type="number"
                    min={0}
                    {...register("carousel_order")}
                  />
                  {errors.carousel_order && (
                    <p className="text-xs text-destructive">
                      {errors.carousel_order.message}
                    </p>
                  )}
                </div>
              </div>

              {submitError && (
                <div
                  role="alert"
                  className="flex items-start gap-2 rounded-md border border-destructive/30 bg-destructive/5 p-3 text-sm text-destructive"
                >
                  <AlertCircle className="mt-0.5 h-4 w-4 shrink-0" />
                  <span>{submitError}</span>
                </div>
              )}

              <div className="flex flex-wrap items-center gap-2">
                <Button
                  type="submit"
                  disabled={isUploading || submittingMut.isPending}
                >
                  {(submittingMut.isPending || isUploading) && (
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  )}
                  {isUploading
                    ? "Uploading…"
                    : submittingMut.isPending
                      ? isEdit
                        ? "Saving…"
                        : "Creating…"
                      : isEdit
                        ? "Update album"
                        : "Create album"}
                </Button>
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => navigate("/albums")}
                  disabled={submittingMut.isPending}
                >
                  Cancel
                </Button>
              </div>
            </form>
          )}
        </CardContent>
      </Card>
    </div>
  )
}
